import { useMemo, useState } from "react";
import { ApplicationButton, ApplicationLocalNavigation, ApplicationStateRegion } from "../../../components/application";
import { DataTable } from "../../../components/data-display";

export const G3_PARTICIPANT_EVIDENCE_COVERAGE = Object.freeze([
  ["Program / Stage / Listening Session progress", "PRESENT_AND_RENDERED"],
  ["four Stage 3 Reflection completion states", "PRESENT_AND_RENDERED"],
  ["Sessions /8 evidence inputs", "PRESENT_AND_RENDERED"],
  ["Practice Day / Spacing evidence", "PRESENT_AND_RENDERED"],
  ["participant perceptual ratings", "PRESENT_AND_RENDERED"],
  ["reference central value / population SD / N", "PRESENT_AND_RENDERED"],
  ["repeated-judgment / probe evidence", "PRESENT_AND_RENDERED"],
  ["Reflection text + selected source Voice + submission time", "PRESENT_AND_RENDERED"],
  ["Stage 4 execution and released participant evidence", "PRESENT_AND_RENDERED"],
  ["learner confidence evidence", "PRESENT_AND_RENDERED"],
  ["target playback evidence", "PRESENT_AND_RENDERED"],
  ["anchor replay evidence", "PRESENT_AND_RENDERED"],
  ["listening-condition acknowledgement", "PRESENT_AND_RENDERED"],
  ["first-open → locked-submit duration", "PRESENT_AND_RENDERED"],
  ["Listening Session completion dates", "PRESENT_AND_RENDERED"],
  ["attention / review flags", "PRESENT_AND_RENDERED"],
  ["export readiness", "PRESENT_AND_RENDERED"],
]);

const NAVIGATION = [
  ["summary", "Summary"], ["practice", "Practice results"], ["reflections", "Reflections & probes"],
  ["final", "Final rating"], ["process", "Process evidence"],
].map(([value, label]) => ({ value, label, id: `participant-${value}-tab`, controls: `participant-${value}-panel` }));
const PARAMETERS = ["Overall Severity", "Roughness", "Breathiness", "Strain"];
const component = (evidence, key) => evidence?.components?.find(row => row.key === key);
const items = (evidence, key) => component(evidence, key)?.items || [];
const value = (evidence, key) => component(evidence, key)?.value ?? 0;
const stage4Label = state => ({ completed_results_released: "Completed — results released", completed_results_pending: "Completed — results pending", in_progress: "In progress", not_started: "Not started" }[state] || "Not started");
const dateTime = input => input ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(input)) : "—";
export const elapsedTime = input => {
  const milliseconds = Number.parseFloat(input);
  if (!Number.isFinite(milliseconds)) return "—";
  const seconds = milliseconds / 1000;
  const displayedSeconds = Number(seconds.toFixed(3));
  if (seconds < 60) return `${displayedSeconds} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${Number((displayedSeconds - minutes * 60).toFixed(3))} s`;
};

export const parseParticipantResult = item => {
  const [stage, session, voice, parameter] = item.label.split(" · ");
  const match = item.value.match(/^Learner rating ([^·]+) · Central value ([^·]+) · Spread \(SD\) ([^·]+) · Reference ratings ([^·]+)$/);
  return match ? { id: item.label, stage, session, voice, parameter, learnerRating: match[1].trim(), centralValue: match[2].trim(), spread: match[3].trim(), referenceN: match[4].trim() } : null;
};

const groupResults = rows => {
  const stages = new Map();
  for (const row of rows) {
    if (!stages.has(row.stage)) stages.set(row.stage, new Map());
    const sessions = stages.get(row.stage);
    if (!sessions.has(row.session)) sessions.set(row.session, []);
    sessions.get(row.session).push(row);
  }
  return stages;
};
const RESULT_COLUMNS = [
  { key: "voice", label: "Voice" }, { key: "parameter", label: "Parameter" },
  { key: "learnerRating", label: "Learner rating" }, { key: "centralValue", label: "Central value" },
  { key: "spread", label: "Spread (SD)" }, { key: "referenceN", label: "Reference N" },
];
const FINAL_COLUMNS = RESULT_COLUMNS.slice(1);

const parseAttentionEvent = item => {
  const [stage, session, voice] = item.label.split(" · ");
  const segments = item.value.split(" · ");
  const field = prefix => segments.find(segment => segment.startsWith(prefix))?.slice(prefix.length) || "unavailable";
  return {
    id: item.label, stage, session, voice,
    signals: field("Review signals: "),
    elapsedMilliseconds: field("Elapsed milliseconds: "),
    targetPlays: field("Target plays: "),
    notes: segments.filter(segment => segment.startsWith("Evidence note: "))
      .map(segment => segment.slice("Evidence note: ".length)),
  };
};

const attentionDuration = input => {
  const milliseconds = Number(input);
  if (!Number.isFinite(milliseconds)) return "Timing unavailable";
  return elapsedTime(`${milliseconds} ms`);
};

const ATTENTION_COLUMNS = [
  { key: "session", label: "Listening session" },
  { key: "voice", label: "Voice" },
  { key: "reviewSignal", label: "Review signal" },
  { key: "evidence", label: "Evidence" },
];

const reviewStateLabel = state => ({
  unreviewed: "Unreviewed",
  reviewed_no_adjustment: "Reviewed — no adjustment",
  reviewed_with_adjustment: "Reviewed — with adjustment",
}[state] || "Unreviewed");

const operationKey = () => window.crypto?.randomUUID?.() ||
  `byce-review-${Date.now()}-${Math.random().toString(16).slice(2)}`;

function AttentionReviewEvent({ event, canReview, onRecord }) {
  const [editing, setEditing] = useState(false);
  const [rationale, setRationale] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async submitEvent => {
    submitEvent.preventDefault();
    if (!rationale.trim()) { setError("Enter a rationale before recording the review."); return; }
    setState("pending"); setError("");
    try {
      await onRecord({ event, rationale: rationale.trim(), idempotencyKey: operationKey() });
      setRationale(""); setState("success");
    } catch (_error) {
      setState("error"); setError("The review could not be recorded. Refresh before trying again.");
    }
  };
  return <article className="av-byce-attention-review-event">
    <header><h6>{event.sessionLabel} · Voice {event.trialOrder}</h6>
      <span>{reviewStateLabel(event.reviewState)}</span></header>
    <p>{event.flags.length ? event.flags.map(flag => flag === "zero_target_plays"
      ? "No target playback recorded" : "Submitted in under 5 seconds").join(" · ")
      : "Attention evidence is incomplete"}</p>
    <p>{event.timing.availability === "available" ? attentionDuration(event.timing.elapsedMilliseconds)
      : "Timing unavailable"} · {event.playback.availability === "available"
      ? `${event.playback.targetPlayCount} target ${event.playback.targetPlayCount === 1 ? "play" : "plays"}`
      : "Playback unavailable"}</p>
    {event.history.length ? <details><summary>Review history ({event.history.length})</summary>
      <ol>{event.history.map(item => <li key={item.decisionId}><strong>Revision {item.revision}</strong>
        {` · ${dateTime(item.createdAt)} · ${item.rationale}`}</li>)}</ol></details> : null}
    {canReview && !editing ? <ApplicationButton onClick={() => setEditing(true)} size="compact" variant="secondary">{event.currentDecisionId ? "Correct review" : "Review response"}</ApplicationButton> : null}
    {canReview && editing ? <form onSubmit={submit}>
      <label htmlFor={`attention-rationale-${event.eventKey}`}>Review rationale</label>
      <textarea id={`attention-rationale-${event.eventKey}`} value={rationale}
        onChange={changeEvent => { setRationale(changeEvent.target.value); setError(""); }} />
      {error ? <p role="alert">{error}</p> : null}
      {state === "success" ? <p role="status">Review recorded.</p> : null}
      <div className="av-byce-instructor-action__buttons"><ApplicationButton disabled={state === "pending"} size="compact" type="submit">
        {state === "pending" ? "Recording review…" : event.currentDecisionId ? "Record corrected review" : "Record reviewed"}
      </ApplicationButton><ApplicationButton onClick={() => setEditing(false)} size="compact" variant="quiet">Cancel</ApplicationButton></div>
    </form> : null}
    {!canReview ? <p>Review authority has not been granted.</p> : null}
  </article>;
}

function AttentionEvidence({ evidence, attentionReviews, onRecordAttentionReview }) {
  const state = value(evidence, "attention-evidence");
  const count = key => component(evidence, key)?.value || 0;
  const shortCount = count("attention-very-short-event-count");
  const zeroPlayCount = count("attention-zero-play-event-count");
  const playbackIncompleteCount = count("attention-playback-incomplete-event-count");
  const timingIncompleteCount = count("attention-timing-incomplete-event-count");
  const groupedEvents = new Map();
  for (const event of items(evidence, "attention-events").map(parseAttentionEvent)) {
    const rows = groupedEvents.get(event.stage) || [];
    rows.push({
      ...event,
      reviewSignal: event.signals === "None" ? "Evidence incomplete" : event.signals,
      evidence: [
        attentionDuration(event.elapsedMilliseconds),
        event.targetPlays === "unavailable" ? "Playback unavailable" : `${event.targetPlays} target ${Number(event.targetPlays) === 1 ? "play" : "plays"}`,
        ...event.notes,
      ].join(" · "),
    });
    groupedEvents.set(event.stage, rows);
  }
  const summaryLines = state === "no_flags"
    ? ["No responses require review"]
    : [
      `${shortCount} ${shortCount === 1 ? "response" : "responses"} submitted in under 5 seconds`,
      `${zeroPlayCount} ${zeroPlayCount === 1 ? "response" : "responses"} with no target playback`,
      ...(playbackIncompleteCount ? [`Playback evidence incomplete for ${playbackIncompleteCount} ${playbackIncompleteCount === 1 ? "response" : "responses"}`] : []),
      ...(timingIncompleteCount ? [`Timing evidence incomplete for ${timingIncompleteCount} ${timingIncompleteCount === 1 ? "response" : "responses"}`] : []),
    ];
  return <div className="av-byce-process-review">
    <p>These responses were flagged for instructor review because of unusually short response time or missing target playback. A flag does not affect the grade automatically.</p>
    <span className="av-byce-attention-summary">{summaryLines.map(line => <span key={line}>{line}</span>)}</span>
    {groupedEvents.size ? <details className="av-byce-attention-disclosure"><summary>Show response evidence</summary><div className="av-byce-attention-groups">{[...groupedEvents].map(([stage, rows]) =>
      <section key={stage}><h5>{stage}</h5><DataTable caption={`${stage} responses to review`} density="compact" minWidth="42rem" rowKey="id" rows={rows} columns={ATTENTION_COLUMNS} /></section>
    )}</div></details> : null}
    {attentionReviews?.events?.length ? <div className="av-byce-attention-review-events">
      {attentionReviews.events.map(event => <AttentionReviewEvent key={event.eventKey} event={event}
        canReview={attentionReviews.canReview} onRecord={onRecordAttentionReview} />)}
    </div> : null}
  </div>;
}

function EngagementAdjustment({ adjustment, onRecord }) {
  const [editing, setEditing] = useState(false);
  const [deduction, setDeduction] = useState(String(adjustment?.deductionPoints ?? 0));
  const [basis, setBasis] = useState(adjustment?.basisReviewDecisionIds || []);
  const [rationale, setRationale] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  if (!adjustment) return null;
  const focusResponsesToReview = () => {
    const target = document.getElementById("responses-to-review");
    target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    target?.focus?.();
  };
  const submit = async event => {
    event.preventDefault();
    const points = Number(deduction);
    if (points > 0 && !basis.length) {
      setError("First review one or more flagged responses above before applying a deduction.");
      focusResponsesToReview(); return;
    }
    if (points > 0 && !rationale.trim()) {
      setError("Enter a rationale for this reviewed deduction."); return;
    }
    setState("pending"); setError("");
    try {
      await onRecord({ deductionPoints: points, basisReviewDecisionIds: points ? basis : [],
        rationale: rationale.trim(), expectedCurrentDecisionId: adjustment.currentDecisionId,
        idempotencyKey: operationKey() });
      setRationale(""); setState("success");
    } catch (_error) {
      setState("error"); setError("The reviewed engagement adjustment could not be recorded. Refresh before trying again.");
    }
  };
  return <section className="av-byce-engagement-adjustment" aria-label="Engagement adjustment">
    <h4>Engagement adjustment</h4>
    <p>Attention flags do not automatically affect the grade. Base Grade remains unchanged; an explicit reviewed deduction produces a separate Adjusted Grade.</p>
    <p><strong>Current deduction:</strong> {adjustment.deductionPoints} points</p>
    {adjustment.rationale ? <p><strong>Current rationale:</strong> {adjustment.rationale}</p> : null}
    {adjustment.history?.length ? <details><summary>Adjustment history ({adjustment.history.length})</summary>
      <ol>{adjustment.history.map(row => <li key={row.decisionId}>
        <strong>Revision {row.revision}: {row.deductionPoints} points</strong>
        {` · ${dateTime(row.createdAt)}${row.rationale ? ` · ${row.rationale}` : ""}`}
      </li>)}</ol></details> : null}
    {adjustment.canAdjust && !editing ? <ApplicationButton onClick={() => setEditing(true)} size="compact" variant="secondary">Adjust grade</ApplicationButton> : null}
    {adjustment.canAdjust && editing ? <form onSubmit={submit}>
      <fieldset className="av-byce-choice-group"><legend>Deduction points</legend>{[0, 1, 2, 3, 4].map(value =>
        <label key={value}><input checked={deduction === String(value)} name="engagement-deduction"
          onChange={() => { setDeduction(String(value)); setError(""); }} type="radio" value={value} />{value === 0 ? "No deduction" : `${value} ${value === 1 ? "point" : "points"}`}</label>)}</fieldset>
      {Number(deduction) > 0 ? <fieldset><legend>Reviewed responses supporting this deduction</legend>
        {adjustment.reviewedEvents.length ? adjustment.reviewedEvents.map(event =>
          <label key={event.reviewDecisionId}><input checked={basis.includes(event.reviewDecisionId)}
            onChange={change => setBasis(current => change.target.checked
              ? [...new Set([...current, event.reviewDecisionId])]
              : current.filter(id => id !== event.reviewDecisionId))} type="checkbox" />
            {event.stageLabel} · {event.sessionLabel} · Voice {event.trialOrder}</label>)
          : <div><p role="alert">First review one or more flagged responses above before applying a deduction.</p>
            <ApplicationButton onClick={focusResponsesToReview} size="compact" variant="link">Review responses above</ApplicationButton></div>}
      </fieldset> : null}
      <label htmlFor="engagement-adjustment-rationale">Rationale {Number(deduction) > 0 ? "(required)" : "(optional)"}</label>
      <textarea id="engagement-adjustment-rationale" value={rationale}
        onChange={event => { setRationale(event.target.value); setError(""); }} />
      {error ? <p role="alert">{error}</p> : null}
      {state === "success" ? <p role="status">Reviewed engagement adjustment recorded.</p> : null}
      <div className="av-byce-instructor-action__buttons"><ApplicationButton disabled={state === "pending"} size="compact" type="submit">
        {state === "pending" ? "Recording adjustment…" : adjustment.currentDecisionId
          ? "Save corrected adjustment" : "Save adjustment"}
      </ApplicationButton><ApplicationButton onClick={() => setEditing(false)} size="compact" variant="quiet">Cancel</ApplicationButton></div>
    </form> : null}
    {!adjustment.canAdjust ? <p>Result-adjustment authority has not been granted.</p> : null}
  </section>;
}

function Summary({ evidence, attentionReviews, engagementAdjustment, onNavigate }) {
  const stageRows = items(evidence, "stage-progress").map((row, index) => ({
    id: row.label, stage: row.label, sessions: index < 3 ? row.value.replace("of", "/") : "—",
    reflection: index === 2 ? `${value(evidence, "completed-reflections")} / 4` : "—",
    finalRating: index === 3 ? row.value.replace("of", "/") : "—",
    result: index === 3 && value(evidence, "stage-4-execution") === "completed_results_released" ? "Results released" : "—",
  }));
  const spacingRows = items(evidence, "practice-day-projection").map(row => ({ label: row.label, value: row.value.split(" · ")[0] }));
  const spacing = spacingRows.length ? <span className="av-byce-spacing-lines">{spacingRows.map(row => <span key={row.label}>{row.label}: {row.value}</span>)}</span> : "Practice Day evidence unavailable";
  const spacingResultState = value(evidence, "spacing-result");
  const spacingPoints = component(evidence, "spacing-points")?.value;
  const spacingAvailable = spacingResultState === "available" && Number.isFinite(spacingPoints);
  const reflectionResultState = value(evidence, "reflection-result");
  const reflectionPoints = component(evidence, "reflection-points")?.value;
  const reflectionAvailable = reflectionResultState === "available" && Number.isFinite(reflectionPoints);
  const sessionsResultState = value(evidence, "sessions-result");
  const sessionsPoints = component(evidence, "sessions-points")?.value;
  const sessionsAvailable = sessionsResultState === "available" && Number.isFinite(sessionsPoints);
  const completedEligibleSessions = value(evidence, "eligible-practice-sessions-completed");
  const finalRatingResultState = value(evidence, "final-rating-result");
  const finalRatingPoints = component(evidence, "final-rating-points")?.value;
  const finalRatingAvailable = finalRatingResultState === "available" && Number.isFinite(finalRatingPoints);
  const baseGradeResultState = value(evidence, "base-grade-result");
  const baseGradePoints = component(evidence, "base-grade-points")?.value;
  const baseGradeAvailable = baseGradeResultState === "available" && Number.isFinite(baseGradePoints);
  const adjustedGradePoints = component(evidence, "adjusted-grade-points")?.value;
  const deductionPoints = component(evidence, "engagement-deduction-points")?.value;
  const adjustmentActive = value(evidence, "engagement-adjustment-active") === "active";
  const displayPoints = points => Number.isInteger(points) ? String(points) : points.toFixed(1);
  const educationRows = [
    { id: "sessions", component: "Sessions", evidence: `${Number.isFinite(completedEligibleSessions) ? completedEligibleSessions : "—"} of 11 eligible independent sessions completed`, score: sessionsAvailable ? `${sessionsPoints} /8` : "— /8", status: sessionsAvailable ? "Governed result available" : sessionsResultState === "incomplete_evidence" ? "Incomplete Session evidence" : "Unavailable until Phase 5" },
    { id: "spacing", component: "Spacing", evidence: spacing, score: spacingAvailable ? `${spacingPoints} /8` : "— /8", status: spacingAvailable ? "Governed result available" : spacingResultState === "incomplete_evidence" ? "Incomplete calendar evidence" : "Descriptive evidence only" },
    { id: "reflection", component: "Reflection", evidence: `${value(evidence, "completed-reflections")} of 4 submitted`, score: reflectionAvailable ? `${reflectionPoints} /8` : "— /8", status: reflectionAvailable ? "Governed result available" : reflectionResultState === "incomplete_evidence" ? "Incomplete Reflection evidence" : "Unavailable until Phase 5" },
    { id: "final", component: "Final rating", evidence: stage4Label(value(evidence, "stage-4-execution")), score: finalRatingAvailable ? `${displayPoints(finalRatingPoints)} /16` : "— /16", status: finalRatingAvailable ? "Governed result available" : finalRatingResultState === "incomplete_evidence" ? "Incomplete Final rating evidence" : "Unavailable until Phase 5" },
  ];
  return <div className="av-byce-participant-workspace__sections">
    <section><h4>Grade summary</h4><dl className="av-byce-grade-summary"><div><dt>Base Grade</dt><dd>{baseGradeAvailable ? `${displayPoints(baseGradePoints)} / 40` : "—"}</dd></div><div><dt>Engagement deduction</dt><dd>{adjustmentActive ? `−${deductionPoints}` : "None"}</dd></div>{adjustmentActive ? <div><dt>Adjusted Grade</dt><dd>{Number.isFinite(adjustedGradePoints) ? `${displayPoints(adjustedGradePoints)} / 40` : "—"}</dd></div> : null}</dl><ApplicationButton onClick={() => onNavigate("process")} size="compact" variant="link">Review engagement</ApplicationButton></section>
    {(attentionReviews?.events || []).some(event => event.reviewState === "unreviewed") ? <section className="av-byce-attention-cta"><p><strong>{attentionReviews.events.filter(event => event.reviewState === "unreviewed").length} responses need instructor review</strong></p><ApplicationButton onClick={() => onNavigate("process")} size="compact" variant="secondary">Review responses →</ApplicationButton></section> : null}
    <section><h4>Practice progress</h4><DataTable caption="Stage progress" captionMode="visually-hidden" density="compact" minWidth="46rem" rowKey="id" rows={stageRows} columns={[{ key: "stage", label: "Stage" }, { key: "sessions", label: "Listening Sessions" }, { key: "reflection", label: "Reflections" }, { key: "finalRating", label: "Final rating" }, { key: "result", label: "Result" }]} /></section>
    <section><h4>Educational components</h4><DataTable caption="Educational components" captionMode="visually-hidden" density="compact" minWidth="44rem" rowKey="id" rows={educationRows} columns={[{ key: "component", label: "Component" }, { key: "evidence", label: "Evidence" }, { key: "score", label: "Score" }, { key: "status", label: "Status" }]} /></section>
  </div>;
}

function PracticeResults({ evidence }) {
  const grouped = groupResults(items(evidence, "practice-results").map(parseParticipantResult).filter(Boolean));
  if (!grouped.size) return <ApplicationStateRegion status="empty" message="Practice results are unavailable." />;
  return <div className="av-byce-participant-workspace__sections">{[...grouped].map(([stage, sessions]) => <section key={stage}><h4>{stage}</h4>{[...sessions].map(([session, rows]) => <details key={session}><summary>{session}</summary><DataTable caption={`${stage} · ${session} results`} density="compact" minWidth="48rem" rowKey="id" rows={rows} columns={RESULT_COLUMNS} /></details>)}</section>)}</div>;
}

const probeLabel = recurrenceId => {
  const match = recurrenceId.match(/^byce-g3-probe-(\d)-(\d)@1$/);
  return match ? `Session 2.${match[1]} → 3.${match[1]} · Repeated Voice ${match[2]}` : "Repeated judgment";
};
const parseProbe = item => ({ label: probeLabel(item.label), rows: item.value.split(" · ").slice(1, -1).map(part => { const match = part.match(/^(Overall Severity|Roughness|Breathiness|Strain) (\d+(?:\.\d+)?)$/); return match ? { id: `${item.label}-${match[1]}`, parameter: match[1], difference: match[2] } : null; }).filter(Boolean) });
const parseReflection = (item, index) => {
  const match = item.value.match(/^(Voice [12]|Voice unavailable) · Submitted ([^·]+) · ([\s\S]*)$/);
  return match ? { id: `${item.label}-${index + 1}`, label: `Reflection 3.${index + 1}`, selectedVoice: match[1], submittedAt: match[2].trim(), text: match[3] } : null;
};

function ReflectionOverride({ item, canOverride, onRecord }) {
  const [editing, setEditing] = useState(false);
  const [points, setPoints] = useState(String(item.effectivePoints ?? item.canonicalPoints ?? 0));
  const [rationale, setRationale] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const submit = async event => {
    event.preventDefault();
    if (!rationale.trim()) { setError("Enter a rationale for this exceptional override."); return; }
    setState("pending"); setError("");
    try {
      await onRecord({ item, points: Number(points), rationale: rationale.trim(),
        idempotencyKey: operationKey() });
      setRationale(""); setState("success");
    } catch (_error) {
      setState("error");
      setError("The Reflection override could not be recorded. Refresh before trying again.");
    }
  };
  return <section className="av-byce-reflection-override" aria-label={`Exceptional points override for ${item.label}`}>
    <p><strong>Canonical points:</strong> {item.canonicalPoints} /{item.maxPoints}</p>
    <p><strong>Effective points:</strong> {item.effectivePoints} /{item.maxPoints}
      {item.overrideActive ? " — explicit override active" : " — no override"}</p>
    {item.history?.length ? <details><summary>Override history ({item.history.length})</summary>
      <ol>{item.history.map(row => <li key={row.decisionId}><strong>Revision {row.revision}: {row.points} points</strong>
        {` · ${dateTime(row.createdAt)} · ${row.rationale}`}</li>)}</ol></details> : null}
    {canOverride && !editing ? <ApplicationButton onClick={() => setEditing(true)} size="compact" variant="secondary">Adjust points</ApplicationButton> : null}
    {canOverride && editing ? <form onSubmit={submit}>
      <fieldset><legend>Exceptional Reflection points</legend>{[0, 1, 2].map(value =>
        <label key={value}><input checked={points === String(value)} name={`reflection-points-${item.stepKey}`}
          onChange={() => setPoints(String(value))} type="radio" value={value} />{value}</label>)}</fieldset>
      <label htmlFor={`reflection-rationale-${item.stepKey}`}>Required rationale</label>
      <textarea id={`reflection-rationale-${item.stepKey}`} value={rationale}
        onChange={event => { setRationale(event.target.value); setError(""); }} />
      {error ? <p role="alert">{error}</p> : null}
      {state === "success" ? <p role="status">Reflection override recorded.</p> : null}
      <div className="av-byce-instructor-action__buttons"><ApplicationButton disabled={state === "pending"} size="compact" type="submit">{state === "pending" ? "Saving adjustment…" : "Save adjustment"}</ApplicationButton><ApplicationButton onClick={() => setEditing(false)} size="compact" variant="quiet">Cancel</ApplicationButton></div>
    </form> : null}
    {!canOverride ? <p>Reflection adjustment authority has not been granted.</p> : null}
  </section>;
}

function ReflectionsAndProbes({ evidence, reflectionOverrides, onRecordReflectionOverride }) {
  const probes = items(evidence, "probe-consistency").map(parseProbe);
  const reflections = items(evidence, "reflection-evidence").map(parseReflection).filter(Boolean);
  const overrideByLabel = new Map((reflectionOverrides?.items || []).map(item => [item.label, item]));
  return <div className="av-byce-participant-workspace__sections">
    <section><h4>Probe consistency</h4>{probes.length ? probes.map(probe => <details key={probe.label}><summary>{probe.label}</summary><DataTable caption={`${probe.label} absolute VAS-point differences`} density="compact" rowKey="id" rows={probe.rows} columns={[{ key: "parameter", label: "Parameter" }, { key: "difference", label: "Absolute difference" }]} /></details>) : <p>No repeated-judgment evidence is available.</p>}</section>
    <section><h4>Reflections</h4>{reflections.length ? <div className="av-byce-reflection-records">{reflections.map(reflection => {
      const override = overrideByLabel.get(reflection.label);
      return <article key={reflection.id}><h5>{reflection.label}</h5><dl><div><dt>Selected</dt><dd>{reflection.selectedVoice}</dd></div><div><dt>Submitted</dt><dd>{dateTime(reflection.submittedAt)}</dd></div></dl><p>{reflection.text}</p>
        {override ? <ReflectionOverride item={override} canOverride={reflectionOverrides.canOverride}
          onRecord={onRecordReflectionOverride} /> : null}</article>;
    })}</div> : <p>No submitted Reflections are available.</p>}</section>
  </div>;
}

function FinalRating({ evidence }) {
  const voices = useMemo(() => {
    const groups = new Map();
    for (const row of items(evidence, "final-rating-results").map(parseParticipantResult).filter(Boolean)) {
      if (!groups.has(row.voice)) groups.set(row.voice, []);
      groups.get(row.voice).push(row);
    }
    return [...groups].map(([voice, rows]) => ({ voice, rows: PARAMETERS.map(parameter => rows.find(row => row.parameter === parameter)).filter(Boolean) }));
  }, [evidence]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected = voices[selectedIndex];
  return <section className="av-byce-participant-workspace__sections"><div><h4>Final rating</h4><p>{stage4Label(value(evidence, "stage-4-execution"))}</p></div>{selected ? <><div aria-label="Final rating Voices" className="av-byce-cohort-voice-navigation">{voices.map((voice, index) => <button aria-current={index === selectedIndex ? "true" : undefined} aria-label={voice.voice} key={voice.voice} onClick={() => setSelectedIndex(index)} type="button">{index + 1}</button>)}</div><h5>{selected.voice}</h5><DataTable caption={`${selected.voice} final rating evidence`} density="compact" minWidth="42rem" rowKey="id" rows={selected.rows} columns={FINAL_COLUMNS} /></> : <ApplicationStateRegion status="empty" message="Released final-rating evidence is unavailable." />}</section>;
}

function ProcessEvidence({ evidence, attentionReviews, onRecordAttentionReview, engagementAdjustment, onRecordEngagementAdjustment }) {
  const progress = items(evidence, "listening-session-progress");
  const dates = new Map(items(evidence, "completed-session-dates").map(row => [row.label, row.value]));
  const acknowledgements = new Map(items(evidence, "listening-condition-evidence").map(row => [row.label, row.value]));
  const timings = items(evidence, "open-to-submit-evidence");
  const rows = progress.map(row => ({ id: row.stepKey || row.label, label: row.label, stage: row.label.split(" · ")[0], session: row.label.split(" · ").slice(1).join(" · "), completed: row.value, conditions: acknowledgements.get(row.label) || "Not recorded", completedAt: dates.get(row.label) || null }));
  return <div className="av-byce-participant-workspace__sections">
    <section><h4 id="responses-to-review" tabIndex="-1">Responses to review</h4><AttentionEvidence evidence={evidence}
      attentionReviews={attentionReviews} onRecordAttentionReview={onRecordAttentionReview} /></section>
    <EngagementAdjustment adjustment={engagementAdjustment} onRecord={onRecordEngagementAdjustment} />
    <DataTable caption="Process evidence summary" density="compact" rowKey="id" rows={[
      { id: "voices", evidence: "Rated Voice events", value: value(evidence, "rated-voice-events") }, { id: "ratings", evidence: "Parameter ratings submitted", value: value(evidence, "rating-evidence") },
      { id: "confidence", evidence: "Confidence judgments", value: value(evidence, "confidence-evidence") }, { id: "targets", evidence: "Target plays", value: value(evidence, "target-plays") }, { id: "anchors", evidence: "Anchor plays", value: value(evidence, "anchor-use") },
    ]} columns={[{ key: "evidence", label: "Evidence" }, { key: "value", label: "Count" }]} />
    <section><h4>Listening Sessions</h4><div className="av-byce-process-sessions">{rows.map(row => <details key={row.id}><summary>{row.label}</summary><dl><div><dt>Completed</dt><dd>{row.completedAt ? dateTime(row.completedAt) : row.completed}</dd></div><div><dt>Listening conditions</dt><dd>{row.conditions}</dd></div></dl>{timings.some(item => item.label.startsWith(`${row.label} · `)) ? <details><summary>View Voice timing</summary><DataTable caption={`${row.label} Voice timing`} density="compact" rowKey="label" rows={timings.filter(item => item.label.startsWith(`${row.label} · `)).map(item => ({ label: item.label.split(" · ").at(-1), duration: elapsedTime(item.value) }))} columns={[{ key: "label", label: "Voice" }, { key: "duration", label: "Open to submit" }]} /></details> : null}</details>)}</div></section>
  </div>;
}

export default function ByceG3ParticipantEvidence({ evidence, attentionReviews,
  onRecordAttentionReview, reflectionOverrides, onRecordReflectionOverride,
  engagementAdjustment, onRecordEngagementAdjustment }) {
  const [section, setSection] = useState("summary");
  const active = NAVIGATION.find(item => item.value === section);
  return <div className="av-byce-participant-workspace">
    <ApplicationLocalNavigation ariaLabel="Participant evidence" items={NAVIGATION} value={section} onChange={setSection} />
    <div aria-labelledby={active.id} id={active.controls} role="tabpanel">
      {section === "summary" ? <Summary evidence={evidence}
        attentionReviews={attentionReviews}
        engagementAdjustment={engagementAdjustment}
        onNavigate={setSection} /> : null}
      {section === "practice" ? <PracticeResults evidence={evidence} /> : null}
      {section === "reflections" ? <ReflectionsAndProbes evidence={evidence}
        reflectionOverrides={reflectionOverrides}
        onRecordReflectionOverride={onRecordReflectionOverride} /> : null}
      {section === "final" ? <FinalRating evidence={evidence} /> : null}
      {section === "process" ? <ProcessEvidence evidence={evidence}
        attentionReviews={attentionReviews} onRecordAttentionReview={onRecordAttentionReview}
        engagementAdjustment={engagementAdjustment}
        onRecordEngagementAdjustment={onRecordEngagementAdjustment} /> : null}
    </div>
  </div>;
}
